import { useState, useRef, useCallback } from 'react';

export function useVoiceRecorder({ onTranscript, onError, maxDuration = 120 }) {
  const [isRecording, setIsRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [transcript, setTranscript] = useState('');
  
  const recognitionRef = useRef(null);
  const intervalRef = useRef(null);

  const startRecording = useCallback(() => {
    // Check browser support
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      onError?.('Voice input not supported. Please use Chrome, Edge, or Safari.');
      return false;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      let finalTranscript = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript;
        }
      }
      
      if (finalTranscript) {
        setTranscript(prev => {
          const newTranscript = prev + (prev ? ' ' : '') + finalTranscript;
          onTranscript?.(newTranscript);
          return newTranscript;
        });
      }
    };

    recognition.onend = () => {
      cleanup();
    };

    recognition.onerror = (event) => {
      cleanup();
      
      if (event.error === 'not-allowed') {
        onError?.('Microphone access denied. Please allow microphone access.');
      } else if (event.error !== 'aborted') {
        onError?.('Voice recording failed. Please try again.');
      }
    };

    try {
      recognition.start();
      recognitionRef.current = recognition;
      setIsRecording(true);
      setDuration(0);

      // Duration timer
      intervalRef.current = setInterval(() => {
        setDuration(d => {
          if (d >= maxDuration) {
            stopRecording();
            return d;
          }
          return d + 1;
        });
      }, 1000);

      return true;
    } catch (error) {
      onError?.('Failed to start recording.');
      return false;
    }
  }, [onTranscript, onError, maxDuration]);

  const stopRecording = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    cleanup();
  }, []);

  const cleanup = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsRecording(false);
  }, []);

  const clearTranscript = useCallback(() => {
    setTranscript('');
    setDuration(0);
  }, []);

  return {
    isRecording,
    duration,
    transcript,
    startRecording,
    stopRecording,
    clearTranscript,
  };
}
